import * as R from "ramda";

import { idx_data_thumb_insert_types } from "../db/datafixtures/datafixtures";

/**
 * Which of the four span buttons (F / C / O / H) the drawing should offer.
 *
 * All four were always drawn, so the driller had to know from memory which
 * ones his grip could even produce: a conventional grip has no insert wall
 * and no interchangeable thumb, yet still asked for a cut span and a
 * hole-to-hole. Hiding what does not apply is the whole point -- but a span
 * that already carries a value stays reachable, because hiding a button is
 * not a reason to strand the number behind it.
 */

const isBlank = (val) => R.isNil(val) || R.trim(String(val)) === "";

const isInsertless = (gripType) =>
  R.trim(String(gripType || "")).toLowerCase() === "no inserts";

/**
 * True when the thumb insert type is part of an interchangeable system (a
 * sleeve plus a swappable insert) rather than a slug glued in for good. Only
 * those have an outer edge and a second hole to measure to, which is what O
 * and H mean.
 */
export const isInterchangeableThumb = (thmInsertType) => {
  // No type chosen yet is "not known", not "not interchangeable". Reading it
  // the other way would pull O and H off every sheet that simply hasn't got
  // to the thumb yet, which is most of them the moment they are created.
  if (isBlank(thmInsertType)) return true;

  const entry = R.find(
    (type) =>
      R.trim(String(type.type || "")).toLowerCase() ===
      R.trim(String(thmInsertType)).toLowerCase()
  )(idx_data_thumb_insert_types);

  // An unknown type is treated as interchangeable: the dataset is the thing
  // most likely to be behind the times, and a spurious button costs less than
  // a missing one.
  return entry ? entry.interchangeable !== false : true;
};

/**
 * @param {object} formValues the parsed spec sheet
 * @returns {{full: boolean, cut: boolean, outer2_cut: boolean, hole_to_hole: boolean}}
 */
export const getSpanVisibility = (formValues) => {
  const values = formValues || {};

  const hasValue = (span) =>
    !isBlank(values[`f1_span_${span}`]) || !isBlank(values[`f2_span_${span}`]);

  // "mostly you won't be installing grips" -- conventional keeps the full
  // span and nothing else.
  const isConventional =
    R.trim(String(values.ball_grip || "")).toLowerCase() === "conventional";

  const noInserts =
    isInsertless(values.f1_grip_type) && isInsertless(values.f2_grip_type);

  const interchangeable = isInterchangeableThumb(values.thm_insert_type);

  return {
    full: true,
    cut: (!isConventional && !noInserts) || hasValue("cut"),
    outer2_cut:
      (!isConventional && interchangeable) || hasValue("outer2_cut"),
    hole_to_hole:
      (!isConventional && interchangeable) || hasValue("hole_to_hole"),
  };
};

/**
 * The span to actually draw. Keeps the sheet off a mode whose button just
 * disappeared -- otherwise picking "conventional" leaves the two big numbers
 * showing a cut span with no way back to the full one.
 */
export const effectiveSelectedSpan = (selectedSpan, visibility) =>
  visibility[selectedSpan] ? selectedSpan : "full";
